import { createContext, Dispatch, ReactChild, useContext, useReducer } from 'react';
import { Job, LifecycleState } from 'src/generated/graphql';

interface EpisodesPageState {
  playingFileId?: string;
  isTrialWarningDialogOpen: boolean;
  loading: boolean;
  filter: {
    name: string;
    sort: string;
    type?: Job['status'];
    lifecycleState?: LifecycleState;
  };
  pagination: {
    totalPages: number;
    offset: number;
    limit: number;
  };
}

type EpisodeContextType = [EpisodesPageState, Dispatch<EpisodesPageReducerAction>];

const EpisodesPageContext = createContext<EpisodeContextType | undefined>(undefined);

export function useEpisodesPageContext() {
  const context = useContext(EpisodesPageContext);
  if (context === undefined) {
    throw new Error('EpisodesPageContext must be used as a child of EpisodesPage');
  }
  return context;
}

interface EpisodesPageProviderProps {
  children: ReactChild;
}

export function EpisodesPageProvider({ children }: EpisodesPageProviderProps) {
  let pagination = { totalPages: 1, offset: 0, limit: 10 };
  try {
    pagination = JSON.parse(localStorage.getItem('episodes-pagination') || '');
  } catch (_error) {
    // no need to do anything, just use default
  }

  let filter = { name: '', sort: 'dateDesc' };
  try {
    filter = JSON.parse(localStorage.getItem('episodes-filter') || '');
  } catch (_error) {
    // no need to do anything, just use default
  }
  const contextReducer = useReducer(reducer, {
    isTrialWarningDialogOpen: false,
    filter,
    loading: false,
    pagination,
  });

  return (
    <EpisodesPageContext.Provider value={contextReducer}>{children}</EpisodesPageContext.Provider>
  );
}

type EpisodesPageReducerAction =
  | { type: 'set-playing-file-id'; id: string | undefined }
  | { type: 'set-page-loading'; loading: EpisodesPageState['loading'] }
  | { type: 'set-pagination'; pagination: Partial<EpisodesPageState['pagination']> }
  | { type: 'set-filter'; filter: Partial<EpisodesPageState['filter']> };

function reducer(state: EpisodesPageState, action: EpisodesPageReducerAction): EpisodesPageState {
  switch (action.type) {
    case 'set-playing-file-id': {
      return { ...state, playingFileId: action.id };
    }
    case 'set-filter': {
      const filter = { ...state.filter, ...action.filter };
      localStorage.setItem('episodes-filter', JSON.stringify(filter));

      // Reset pagination to page 1 when filter type or lifecycleState changes
      const filterChanged =
        filter.type !== state.filter.type || filter.lifecycleState !== state.filter.lifecycleState;

      if (filterChanged) {
        const pagination = { ...state.pagination, offset: 0 };
        localStorage.setItem('episodes-pagination', JSON.stringify(pagination));
        return { ...state, filter, pagination };
      }

      return { ...state, filter };
    }
    case 'set-page-loading': {
      return { ...state, loading: action.loading };
    }
    case 'set-pagination': {
      const pagination = { ...state.pagination, ...action.pagination };
      localStorage.setItem('episodes-pagination', JSON.stringify(pagination));
      return { ...state, pagination };
    }
    default: {
      throw new Error('Unhandled action');
    }
  }
}
